import { Controller } from "@hotwired/stimulus"
import * as XLSX from "xlsx"

export default class extends Controller {
  static targets = ["dataStartRow", "preview", "mappingSlot", "sheetTabs", "sheetNameInput", "importPreview", "scriptNameInput", "submitButton", "answerColumnsInput", "segmentInfo", "segmentHeaderRow", "segmentOverridesInput", "segmentExcludedInput"]
  // NOTE: 割り付け名の行は segmentHeaderRow 入力で指定する（既定 6 行目 = 0-indexed: 5）。
  // その行より上はカテゴリ階層として連結する。指定行にテキストがあってもデータ行で優先順位値が現れない列は対象外。
  static SEGMENT_HEADER_ROW_IDX = 5
  static SEGMENT_PRIORITY_PATTERN = /^[1-9]\d?$|^[◎○△]$|^◯\d+$/
  static values = { url: String, requiredSheet: String }

  static FIELD_LABELS = {
    redistribution_id: "振り直しID", mid: "MID",
    last_name_kana: "セイ", first_name_kana: "メイ",
    last_name: "姓", first_name: "名", phone: "電話番号",
    email: "メールアドレス", postal_code: "郵便番号",
    address_prefecture: "住所（都道府県）", address_detail: "住所（市区町村以降）",
    delivery_panel: "配信パネル", supplier: "アンケートサイト"
  }

  // ヘッダー行のセル値からフィールドを自動判別するパターン（配列内のいずれかに完全一致でマッピング）
  static HEADER_PATTERNS = {
    redistribution_id: ["No", "振り直しID"],
    mid: ["MID"],
    last_name_kana: ["SS1t1"],
    first_name_kana: ["SS1t2"],
    last_name: ["SS1t3"],
    first_name: ["SS1t4"],
    phone: ["SS1t5"],
    email: ["SS1t6"],
    postal_code: ["SS1t7"],
    address_prefecture: ["SS1t8"],
    address_detail: ["SS1t9"],
    delivery_panel: ["配信パネル"],
    supplier: ["アンケートサイト", "Supplier"]
  }

  // 個人情報フィールド（プレビューで「取り込み対象」を判定するための集合。Ruby 側 PERSONAL_INFO_FIELDS と一致させる）
  static PERSONAL_INFO_FIELDS = [
    "last_name", "first_name", "last_name_kana", "first_name_kana",
    "phone", "email", "postal_code", "address_prefecture", "address_detail"
  ]

  connect() {
    this.mappings = {}
    this.currentDataStartRow = 5
    this.segmentNameOverrides = {}
    this.segmentExcludedColumns = []
    this.setFormEnabled(false)
    this.loadExcel()
  }

  getDataStartRowValue() {
    if (this.hasDataStartRowTarget) {
      return this.dataStartRowTarget.value
    }
    return this.currentDataStartRow
  }

  // 割り付け名の行（0-indexed）。入力があればそれを優先し、無ければ既定の 6 行目を使う
  getSegmentHeaderRowIdx() {
    if (this.hasSegmentHeaderRowTarget) {
      const value = parseInt(this.segmentHeaderRowTarget.value)
      if (value > 0) return value - 1
    }
    return this.constructor.SEGMENT_HEADER_ROW_IDX
  }

  // 割り付け行の入力変更時に再検出してプレビューへ反映する
  updateSegmentRow() {
    this.detectSegmentColumns()
    this.detectAnswerColumns()
    this.renderImportPreview()
  }

  async loadExcel() {
    try {
      const response = await fetch(this.urlValue)
      const buffer = await response.arrayBuffer()
      this.workbook = XLSX.read(buffer, { type: "array", cellStyles: true })

      // NOTE: 取り込み対象は requiredSheet で指定したシートに固定する。存在しなければ警告し取り込み不可にする
      if (!this.workbook.SheetNames.includes(this.requiredSheetValue)) {
        this.showRequiredSheetMissing()
        return
      }

      this.selectSheet(this.requiredSheetValue)
      this.setFormEnabled(true)
    } catch (e) {
      this.previewTarget.innerHTML = `<p class="text-red-600 py-8 text-center">Excel ファイルの読み込みに失敗しました: ${e.message}</p>`
    }
  }

  showRequiredSheetMissing() {
    this.setFormEnabled(false)
    const name = this.escapeHtml(this.requiredSheetValue)
    this.previewTarget.innerHTML = `
      <div class="px-4 py-10 text-center">
        <p class="text-red-600 font-semibold">「${name}」シートが見つかりません</p>
        <p class="text-sm text-gray-500 mt-2">この取り込みは「${name}」という名前のシートからのみ行えます。<br>「${name}」シートを含む Excel をアップロードし直してください。</p>
      </div>`
    if (this.hasSheetTabsTarget) this.sheetTabsTarget.innerHTML = ""
  }

  setFormEnabled(enabled) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !enabled
    }
    if (this.hasScriptNameInputTarget) {
      this.scriptNameInputTarget.disabled = !enabled
    }
  }

  selectSheet(sheetName) {
    this.currentSheetName = sheetName
    this.sheet = this.workbook.Sheets[sheetName]

    if (this.hasSheetNameInputTarget) {
      this.sheetNameInputTarget.value = sheetName
    }

    this.mappings = {}
    this.mappingSlotTargets.forEach(slot => this.clearSlot(slot.dataset.field))

    const detected = this.detectDataStartRow()
    if (this.hasDataStartRowTarget) {
      this.dataStartRowTarget.value = detected + 1
    }
    this.currentDataStartRow = detected + 1

    this.autoDetectMappings()
    this.detectAnswerColumns()
    this.fillScriptName()

    this.showLoading()
    requestAnimationFrame(() => {
      this.renderPreview()
      this.renderImportPreview()
    })
  }

  fillScriptName() {
    if (!this.hasScriptNameInputTarget) return
    // 1行目 D列（col index 3）の値を調査名として取得
    const ref = XLSX.utils.encode_cell({ r: 0, c: 3 })
    const cell = this.sheet[ref]
    if (cell && cell.v != null) {
      this.scriptNameInputTarget.value = String(cell.v).trim()
    }
  }

  detectDataStartRow() {
    const sheet = this.sheet
    if (!sheet || !sheet["!ref"]) return 1

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const maxRow = Math.min(range.e.r, 99)
    const maxCol = range.e.c
    const MIN_RUN = 3

    const rows = []
    for (let r = 0; r <= maxRow; r++) {
      let fillCount = 0
      let numericCount = 0
      let hasNumInFirstCols = false
      for (let c = 0; c <= maxCol; c++) {
        const ref = XLSX.utils.encode_cell({ r, c })
        const cell = sheet[ref]
        if (cell && cell.v !== null && cell.v !== undefined && String(cell.v).trim() !== "") {
          fillCount++
          if (typeof cell.v === "number") {
            numericCount++
            if (c <= 1) hasNumInFirstCols = true
          } else if (c <= 1 && typeof cell.v === "string" && /^\d+$/.test(cell.v.trim())) {
            hasNumInFirstCols = true
          }
        }
      }
      rows.push({ fillCount, numericCount, hasNumInFirstCols })
    }

    // 先頭2列（No, MID 等）に数値を持つ行が連続する最初のポイントを探す
    for (let r = 0; r <= maxRow - MIN_RUN + 1; r++) {
      const window = rows.slice(r, r + MIN_RUN)
      if (!window.every(row => row.hasNumInFirstCols && row.fillCount >= 2)) continue
      return r
    }

    // フォールバック: 数値セルを含み、セル数が安定している行
    for (let r = 0; r <= maxRow - MIN_RUN + 1; r++) {
      const window = rows.slice(r, r + MIN_RUN)
      if (!window.every(row => row.numericCount > 0 && row.fillCount >= 2)) continue

      const fills = window.map(row => row.fillCount)
      const tolerance = Math.max(5, Math.ceil(Math.max(...fills) * 0.3))
      if (Math.max(...fills) - Math.min(...fills) <= tolerance) return r
    }

    // 数値がないシートの場合: セル数だけで判定
    for (let r = 0; r <= maxRow - MIN_RUN + 1; r++) {
      const window = rows.slice(r, r + MIN_RUN)
      if (window.every(row => row.fillCount === 0)) continue

      const fills = window.map(row => row.fillCount)
      const mean = fills.reduce((a, b) => a + b, 0) / MIN_RUN
      if (mean < 2) continue
      if (Math.max(...fills) - Math.min(...fills) <= 1) return r
    }

    const firstNonEmpty = rows.findIndex(row => row.fillCount > 0)
    return firstNonEmpty >= 0 ? firstNonEmpty + 1 : 1
  }

  showLoading() {
    const skeletonRows = Array.from({ length: 6 }, (_, i) =>
      `<div class="flex gap-3" style="animation: pulse 1.5s ease-in-out infinite; animation-delay: ${i * 0.08}s">
        <div class="h-3 w-10 rounded bg-gray-200 shrink-0"></div>
        <div class="h-3 rounded bg-gray-200 flex-1"></div>
        <div class="h-3 rounded bg-gray-200 flex-1"></div>
        <div class="h-3 rounded bg-gray-200 flex-1"></div>
        <div class="h-3 rounded bg-gray-100 flex-1"></div>
      </div>`
    ).join("")

    this.previewTarget.innerHTML = `
      <div class="px-4 py-10 space-y-4">
        <div class="flex justify-center mb-6">
          <div class="bounce-loader">
            <div class="dot"></div><div class="dot"></div><div class="dot"></div><div class="dot"></div><div class="dot"></div>
          </div>
        </div>
        ${skeletonRows}
      </div>`
  }

  renderPreview() {
    const sheet = this.sheet
    if (!sheet || !sheet["!ref"]) {
      this.previewTarget.innerHTML = '<p class="text-gray-500 py-8 text-center">データがありません</p>'
      return
    }

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const dataStartRowIdx = (parseInt(this.getDataStartRowValue()) || 5) - 1
    const headerRowIdx = Math.max(dataStartRowIdx - 1, 0)
    const maxRow = Math.min(range.e.r, 99)
    const maxCol = range.e.c
    const merges = sheet["!merges"] || []

    let html = '<table class="min-w-full text-xs border-collapse">'

    html += '<thead class="sticky top-0 z-10"><tr class="bg-gray-100">'
    html += '<th class="px-2 py-1.5 text-center font-medium text-gray-400 border border-gray-300 bg-gray-100 min-w-[40px] sticky left-0 z-20"></th>'
    for (let c = 0; c <= maxCol; c++) {
      html += `<th class="px-2 py-1.5 text-center font-medium text-gray-400 border border-gray-300 bg-gray-100 min-w-[80px]">${XLSX.utils.encode_col(c)}</th>`
    }
    html += '</tr></thead><tbody>'

    for (let r = 0; r <= maxRow; r++) {
      const isPrimaryHeader = r === headerRowIdx
      const isHeaderRange = r >= headerRowIdx && r < dataStartRowIdx
      const displayRow = r + 1
      html += `<tr${isPrimaryHeader ? ' class="ring-2 ring-indigo-400 ring-inset"' : ""}>`
      html += `<td class="px-2 py-1 text-center border border-gray-300 font-mono sticky left-0 z-[5] cursor-pointer hover:bg-indigo-50 transition-colors ${isPrimaryHeader ? "bg-indigo-100 text-indigo-700 font-semibold" : isHeaderRange ? "bg-indigo-50 text-indigo-500" : "bg-gray-50 text-gray-400"}" data-action="click->excel-preview#selectDataStartRow" data-row-index="${r}">${displayRow}</td>`

      for (let c = 0; c <= maxCol; c++) {
        if (this.isMergedHidden(merges, r, c)) continue

        const ref = XLSX.utils.encode_cell({ r, c })
        const cell = sheet[ref]
        const value = cell ? this.formatValue(cell) : ""
        const style = cell?.s ? this.cellStyleToCSS(cell.s) : ""
        const merge = this.getMergeSpan(merges, r, c)
        const spanAttrs = merge ? ` colspan="${merge.cs}" rowspan="${merge.rs}"` : ""

        if (isHeaderRange && value && this.hasMappingSlotTarget) {
          html += `<td class="px-2 py-1 border border-gray-300 whitespace-nowrap"${spanAttrs} style="${style}">`
          html += '<div class="cursor-grab select-none inline-flex items-center gap-1 rounded px-1.5 py-0.5'
          html += ` ${isPrimaryHeader ? "bg-indigo-50 border border-indigo-200 text-indigo-700" : "bg-blue-50 border border-blue-200 text-blue-700"} hover:bg-indigo-100 transition-colors"`
          html += ` draggable="true" data-action="dragstart->excel-preview#dragStart" data-col-index="${c}" data-row-index="${r}">`
          html += '<svg class="w-3 h-3 opacity-40 shrink-0" fill="currentColor" viewBox="0 0 20 20">'
          html += '<circle cx="6" cy="4" r="1.5"/><circle cx="14" cy="4" r="1.5"/>'
          html += '<circle cx="6" cy="10" r="1.5"/><circle cx="14" cy="10" r="1.5"/>'
          html += '<circle cx="6" cy="16" r="1.5"/><circle cx="14" cy="16" r="1.5"/></svg>'
          html += `<span>${this.escapeHtml(value)}</span></div></td>`
        } else {
          html += `<td class="px-2 py-1 border border-gray-300 whitespace-nowrap"${spanAttrs} style="${style}">${this.escapeHtml(value)}</td>`
        }
      }
      html += '</tr>'
    }
    html += '</tbody></table>'
    this.previewTarget.innerHTML = html
  }

  formatValue(cell) {
    if (cell.w) return cell.w
    if (cell.v === null || cell.v === undefined) return ""
    return String(cell.v)
  }

  cellStyleToCSS(s) {
    if (!s) return ""
    let css = ""

    if (s.fgColor?.rgb) {
      css += `background-color:#${s.fgColor.rgb};`
    } else if (s.patternType === "solid" && s.fgColor?.rgb) {
      css += `background-color:#${s.fgColor.rgb};`
    }

    if (s.bold) css += "font-weight:bold;"
    if (s.italic) css += "font-style:italic;"
    if (s.underline) css += "text-decoration:underline;"
    if (s.color?.rgb) css += `color:#${s.color.rgb};`
    if (s.sz) css += `font-size:${s.sz}pt;`

    if (s.alignment) {
      if (s.alignment.horizontal) css += `text-align:${s.alignment.horizontal};`
      if (s.alignment.vertical) css += `vertical-align:${s.alignment.vertical};`
    }

    return css
  }

  isMergedHidden(merges, r, c) {
    return merges.some(m =>
      r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c &&
      !(r === m.s.r && c === m.s.c)
    )
  }

  getMergeSpan(merges, r, c) {
    const m = merges.find(m => m.s.r === r && m.s.c === c)
    if (!m) return null
    return { rs: m.e.r - m.s.r + 1, cs: m.e.c - m.s.c + 1 }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  dragStart(event) {
    const colIndex = event.currentTarget.dataset.colIndex
    const rowIndex = event.currentTarget.dataset.rowIndex
    event.dataTransfer.setData("text/plain", JSON.stringify({ col: colIndex, row: rowIndex }))
    event.dataTransfer.effectAllowed = "copy"
    event.currentTarget.classList.add("opacity-50")
    event.currentTarget.addEventListener("dragend", () => {
      event.currentTarget.classList.remove("opacity-50")
    }, { once: true })
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    event.currentTarget.classList.add("ring-2", "ring-indigo-500", "bg-indigo-50")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50")
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50")

    const data = JSON.parse(event.dataTransfer.getData("text/plain"))
    const colIndex = data.col
    const rowIndex = data.row
    const slotEl = event.currentTarget.closest("[data-field]")
    if (!slotEl) return
    const fieldKey = slotEl.dataset.field

    for (const [key, val] of Object.entries(this.mappings)) {
      if (String(val) === String(colIndex) && key !== fieldKey) {
        delete this.mappings[key]
        this.clearSlot(key)
      }
    }

    this.mappings[fieldKey] = colIndex
    this.fillSlot(slotEl, colIndex)

    const input = slotEl.querySelector("input[type='hidden']")
    if (input) input.value = colIndex

    this.detectAnswerColumns()
    this.renderImportPreview()
  }

  autoDetectMappings() {
    const sheet = this.sheet
    if (!sheet || !sheet["!ref"]) return

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const dataStartRowIdx = (parseInt(this.getDataStartRowValue()) || 5) - 1
    const patterns = this.constructor.HEADER_PATTERNS

    // ヘッダーセル値を収集（データ開始行より上の全行を対象に、列ごとに値の集合を保持）
    // NOTE: ファイルによって「No」「配信パネル」等の表示ラベルと「SS1t1」等の内部コードが
    //       別の行に分かれているため、複数行をスキャンしてどちらでもマッチできるようにする。
    const headerCellsByCol = {}
    for (let r = 0; r < dataStartRowIdx; r++) {
      for (let c = 0; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r, c })
        const cell = sheet[ref]
        if (cell && cell.v !== null && cell.v !== undefined) {
          const val = String(cell.v).trim()
          if (val === "") continue
          if (!headerCellsByCol[c]) headerCellsByCol[c] = []
          headerCellsByCol[c].push(val)
        }
      }
    }

    // パターンマッチングで自動判別（パターン配列内のいずれかに完全一致でマッピング）
    for (const [field, candidates] of Object.entries(patterns)) {
      for (const [colIndex, values] of Object.entries(headerCellsByCol)) {
        if (values.some(v => candidates.includes(v))) {
          this.mappings[field] = colIndex

          const slotEl = this.mappingSlotTargets.find(el => el.dataset.field === field)
          if (slotEl) {
            this.fillSlot(slotEl, colIndex)
            const input = slotEl.querySelector("input[type='hidden']")
            if (input) input.value = colIndex
          }
          break
        }
      }
    }

    // NOTE: アンケートサイト（supplier）は「配信パネルのすぐ右隣にある列」のため、
    //       配信パネルが見つかっていてアンケートサイトが未マッピングなら右隣列を自動採用する。
    //       D 列のようにヘッダーが空でもデータ行に値があれば対象とする。
    if (this.mappings.delivery_panel != null && this.mappings.supplier == null) {
      const nextCol = parseInt(this.mappings.delivery_panel, 10) + 1
      const alreadyMapped = Object.values(this.mappings).some(v => String(v) === String(nextCol))
      if (!alreadyMapped && nextCol <= range.e.c && this.hasAnyValueInColumn(sheet, nextCol, dataStartRowIdx, range.e.r)) {
        this.mappings.supplier = String(nextCol)
        const slotEl = this.mappingSlotTargets.find(el => el.dataset.field === "supplier")
        if (slotEl) {
          this.fillSlot(slotEl, String(nextCol))
          const input = slotEl.querySelector("input[type='hidden']")
          if (input) input.value = String(nextCol)
        }
      }
    }

    // 割り付け列を 6 行目固定で検出
    this.detectSegmentColumns()
  }

  // 指定列のデータ行範囲に 1 件でも非空セルがあるか判定する
  hasAnyValueInColumn(sheet, colIndex, startRow, endRow) {
    for (let r = startRow; r <= endRow; r++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c: colIndex })]
      if (cell && cell.v !== null && cell.v !== undefined && String(cell.v).trim() !== "") {
        return true
      }
    }
    return false
  }

  // 6 行目を走査して割り付け列の候補を抽出し、データ行で優先順位値が現れる列のみ採用する。
  // 上の行（1〜5 行目）にカテゴリ階層がある場合は連結して割り付け名を構築する。
  detectSegmentColumns() {
    this.segmentColumns = new Map()
    // NOTE: 列の検出をやり直すため、手修正した名前と削除指定もリセットする（列との対応がずれるのを防ぐ）
    this.segmentNameOverrides = {}
    this.segmentExcludedColumns = []
    if (this.hasSegmentOverridesInputTarget) this.segmentOverridesInputTarget.value = "{}"
    if (this.hasSegmentExcludedInputTarget) this.segmentExcludedInputTarget.value = "[]"
    const sheet = this.sheet
    if (!sheet || !sheet["!ref"]) {
      this.updateSegmentInfo()
      return
    }

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const merges = sheet["!merges"] || []
    const headerRowIdx = this.getSegmentHeaderRowIdx()
    if (range.e.r < headerRowIdx) {
      this.updateSegmentInfo()
      return
    }

    // 1. 6 行目にテキストがある列を候補に
    const headerTextCols = []
    for (let c = 0; c <= range.e.c; c++) {
      if (this.getResolvedCellText(sheet, merges, headerRowIdx, c)) {
        headerTextCols.push(c)
      }
    }
    if (headerTextCols.length === 0) {
      this.updateSegmentInfo()
      return
    }

    // 2. データ行で優先順位値が現れる列のみ採用（全データ行を走査）
    const dataStartRowIdx = (parseInt(this.getDataStartRowValue()) || 5) - 1
    const sampleEnd = range.e.r
    const pattern = this.constructor.SEGMENT_PRIORITY_PATTERN
    const hits = {}
    const uniques = {}
    const nonMatchNum = {}

    for (let r = dataStartRowIdx; r <= sampleEnd; r++) {
      for (const c of headerTextCols) {
        const ref = XLSX.utils.encode_cell({ r, c })
        const cell = sheet[ref]
        if (!cell || cell.v == null) continue
        const val = String(cell.v).trim()
        if (val === "") continue

        if (pattern.test(val)) {
          hits[c] = (hits[c] || 0) + 1
          if (!uniques[c]) uniques[c] = new Set()
          uniques[c].add(val)
        } else if (typeof cell.v === "number") {
          nonMatchNum[c] = (nonMatchNum[c] || 0) + 1
        }
      }
    }

    const sampleRows = sampleEnd - dataStartRowIdx + 1
    const matchedCols = Object.entries(hits)
      .filter(([col, count]) => {
        const nm = nonMatchNum[col] || 0
        const uc = uniques[col]?.size || 0
        const isSparse = count < sampleRows * 0.5
        const isFlag = uc <= 2 && count >= sampleRows * 0.3
        return count >= 3 && nm <= count && (isSparse || isFlag)
      })
      .map(([col]) => parseInt(col))
      .sort((a, b) => a - b)

    // NOTE: 割り付け列は連続して配置される決まりのため、最大の連続ブロックのみ採用する
    //       （数字回答の設問が誤検出され孤立した列を除外する）
    const candidateCols = this.largestContiguousGroup(matchedCols)

    let seq = 1
    for (const c of candidateCols) {
      const baseText = this.getResolvedCellText(sheet, merges, headerRowIdx, c)
      if (!baseText) continue

      const categories = []
      const seen = new Set()
      for (let r = 0; r < headerRowIdx; r++) {
        const text = this.getResolvedCellText(sheet, merges, r, c)
        if (!text) continue
        if (seen.has(text)) continue
        seen.add(text)
        categories.push(text)
      }

      const fullName = [...categories, baseText].join(" / ")
      this.segmentColumns.set(c, { seq, name: `${seq}. ${fullName}` })
      seq++
    }

    this.updateSegmentInfo()
  }

  getResolvedCellText(sheet, merges, r, c) {
    const merge = merges.find(m => r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c)
    const sourceR = merge ? merge.s.r : r
    const sourceC = merge ? merge.s.c : c
    const ref = XLSX.utils.encode_cell({ r: sourceR, c: sourceC })
    const cell = sheet[ref]
    if (!cell || cell.v === null || cell.v === undefined) return null
    if (cell.v instanceof Date) return null
    const val = String(cell.v).trim()
    if (val === "" || /^\d+(\.\d+)?$/.test(val) || /^\d{4}-\d{2}-\d{2}/.test(val)) return null
    return val
  }

  updateSegmentInfo() {
    if (!this.hasSegmentInfoTarget) return
    const segments = this.segmentColumns ? Array.from(this.segmentColumns.entries()) : []

    if (segments.length === 0) {
      this.segmentInfoTarget.innerHTML = '<span class="text-xs text-gray-400">6 行目にセル文字がある列が見つかりませんでした</span>'
      return
    }

    const colsLetter = segments.map(([c]) => XLSX.utils.encode_col(c)).join(", ")
    let html = `<div class="mb-3"><span class="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">${segments.length}割り付け検出（${colsLetter} 列）</span></div>`
    html += '<p class="text-xs text-gray-500 mb-2">割り付け名は手修正できます。不要な説明文などが含まれる場合は編集してください。</p>'
    html += '<div class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 max-h-64 overflow-y-auto">'
    html += '<div class="space-y-2">'
    let displaySeq = 1
    segments.forEach(([c, info]) => {
      const current = this.segmentNameOverrides[c] !== undefined ? this.segmentNameOverrides[c] : info.name.replace(/^\d+\.\s*/, "")
      html += '<div class="flex items-center gap-2">'
      html += `<span class="inline-flex items-center justify-center shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-[10px] font-bold text-white">${displaySeq}</span>`
      html += `<input type="text" value="${this.escapeAttr(current)}" data-col="${c}" data-action="input->excel-preview#editSegmentName" class="flex-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />`
      html += `<button type="button" data-col="${c}" data-action="excel-preview#removeSegment" title="この割り付けを削除" class="shrink-0 text-gray-400 hover:text-red-600 transition-colors cursor-pointer">`
      html += '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>'
      html += '</button>'
      html += '</div>'
      displaySeq++
    })
    html += '</div></div>'
    this.segmentInfoTarget.innerHTML = html
  }

  removeSegment(event) {
    const col = parseInt(event.currentTarget.dataset.col)
    if (this.segmentColumns) this.segmentColumns.delete(col)
    if (!this.segmentExcludedColumns.includes(col)) this.segmentExcludedColumns.push(col)
    delete this.segmentNameOverrides[col]
    this.updateSegmentInfo()
    this.updateSegmentOverridesInput()
    this.updateSegmentExcludedInput()
    this.detectAnswerColumns()
    this.renderImportPreview()
  }

  updateSegmentExcludedInput() {
    if (!this.hasSegmentExcludedInputTarget) return
    this.segmentExcludedInputTarget.value = JSON.stringify(this.segmentExcludedColumns)
  }

  // 連続した列のうち最大のブロックを返す（割り付け列は連続配置される前提）
  largestContiguousGroup(cols) {
    if (cols.length === 0) return []
    const groups = []
    let current = [ cols[0] ]
    for (let i = 1; i < cols.length; i++) {
      if (cols[i] - cols[i - 1] === 1) {
        current.push(cols[i])
      } else {
        groups.push(current)
        current = [ cols[i] ]
      }
    }
    groups.push(current)
    return groups.reduce((a, b) => (b.length > a.length ? b : a))
  }

  editSegmentName(event) {
    const col = event.target.dataset.col
    this.segmentNameOverrides[col] = event.target.value
    this.updateSegmentOverridesInput()
  }

  updateSegmentOverridesInput() {
    if (!this.hasSegmentOverridesInputTarget) return
    this.segmentOverridesInputTarget.value = JSON.stringify(this.segmentNameOverrides)
  }

  escapeAttr(str) {
    return this.escapeHtml(str).replace(/"/g, "&quot;")
  }

  fillSlot(slotEl, colIndex) {
    const zone = slotEl.querySelector(".drop-zone")
    const fieldKey = slotEl.dataset.field
    const colLetter = XLSX.utils.encode_col(parseInt(colIndex))
    zone.innerHTML = `
      <span class="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700 cursor-pointer hover:bg-indigo-200 transition-colors" data-action="click->excel-preview#scrollToColumn" data-col-index="${colIndex}">
        ${colLetter}列
        <button type="button" class="text-indigo-400 hover:text-indigo-600 cursor-pointer" data-action="click->excel-preview#removeMapping" data-field="${fieldKey}">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </span>`
    zone.classList.remove("border-dashed", "border-gray-300")
    zone.classList.add("border-solid", "border-indigo-200", "bg-indigo-50/50")
  }

  scrollToColumn(event) {
    if (event.target.closest("button")) return

    const colIndex = parseInt(event.currentTarget.dataset.colIndex)
    const table = this.previewTarget.querySelector("table")
    if (!table) return

    // 列ヘッダー（thead の該当 th）を基準にスクロール
    const ths = table.querySelectorAll("thead th")
    const th = ths[colIndex + 1] // +1 は行番号列分
    if (!th) return

    th.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })

    // 該当列をハイライト
    const cells = table.querySelectorAll(`td:nth-child(${colIndex + 2}), th:nth-child(${colIndex + 2})`)
    cells.forEach(cell => cell.classList.add("bg-indigo-100"))
    setTimeout(() => cells.forEach(cell => cell.classList.remove("bg-indigo-100")), 3000)
  }

  clearSlot(fieldKey) {
    const slotEl = this.mappingSlotTargets.find(el => el.dataset.field === fieldKey)
    if (!slotEl) return
    const zone = slotEl.querySelector(".drop-zone")
    zone.innerHTML = '<span class="text-xs text-gray-400">列ヘッダーをドラッグ</span>'
    zone.classList.remove("border-solid", "border-indigo-200", "bg-indigo-50/50")
    zone.classList.add("border-dashed", "border-gray-300")
    const input = slotEl.querySelector("input[type='hidden']")
    if (input) input.value = ""
  }

  removeMapping(event) {
    event.preventDefault()
    event.stopPropagation()
    const fieldKey = event.currentTarget.dataset.field
    delete this.mappings[fieldKey]
    this.clearSlot(fieldKey)
    this.detectAnswerColumns()
    this.renderImportPreview()
  }

  selectDataStartRow(event) {
    const rowIndex = parseInt(event.currentTarget.dataset.rowIndex)
    this.currentDataStartRow = rowIndex + 1
    if (this.hasDataStartRowTarget) {
      this.dataStartRowTarget.value = rowIndex + 1
    }
    this.updateHeaders()
  }

  updateHeaders() {
    this.mappings = {}
    this.mappingSlotTargets.forEach(slot => this.clearSlot(slot.dataset.field))
    this.autoDetectMappings()
    this.detectAnswerColumns()
    this.renderPreview()
    this.renderImportPreview()
  }

  detectAnswerColumns() {
    const sheet = this.sheet
    if (!sheet || !sheet["!ref"]) {
      this.detectedAnswerCols = []
      this.answerGroups = []
      return
    }

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const dataStartRowIdx = (parseInt(this.getDataStartRowValue()) || 5) - 1
    const merges = sheet["!merges"] || []

    const mappedColIndices = new Set(Object.values(this.mappings).map(v => String(v)))
    const segmentColIndices = this.segmentColumns ? new Set(this.segmentColumns.keys()) : new Set()
    const excludedSegmentCols = new Set(this.segmentExcludedColumns || [])

    const answerCols = []
    for (let c = 0; c <= range.e.c; c++) {
      if (mappedColIndices.has(String(c))) continue
      if (segmentColIndices.has(c)) continue
      if (excludedSegmentCols.has(c)) continue

      // データ開始行より上の全行を走査して各列のテキストを収集
      const colTexts = this.collectColumnTexts(sheet, merges, dataStartRowIdx, c)
      if (colTexts.length === 0) continue

      // 最下行のテキストをヘッダー（キー）、残りを説明文として使用
      const header = colTexts[colTexts.length - 1]
      const description = colTexts.length > 1
        ? colTexts.slice(0, -1).join(" / ")
        : null

      answerCols.push({ index: c, header, description })
    }

    this.detectedAnswerCols = answerCols
    this.updateAnswerColumnsInput()
  }

  // データ開始行より上の全行を走査し、該当列のテキストを上から順に収集
  collectColumnTexts(sheet, merges, dataStartRowIdx, colIdx) {
    const texts = []
    const seen = new Set()

    for (let r = 0; r < dataStartRowIdx; r++) {
      // 結合セルの中にいるか確認し、元セルの値を取得
      const merge = merges.find(m =>
        r >= m.s.r && r <= m.e.r && colIdx >= m.s.c && colIdx <= m.e.c
      )
      const sourceRow = merge ? merge.s.r : r
      const sourceCol = merge ? merge.s.c : colIdx
      const ref = XLSX.utils.encode_cell({ r: sourceRow, c: sourceCol })
      const cell = sheet[ref]

      if (cell && cell.v !== null && cell.v !== undefined) {
        const val = String(cell.v).trim()
        // 数値のみの行（データ件数など）や日付値はスキップ
        if (val === "" || /^\d+(\.\d+)?$/.test(val) || /^\d{4}-\d{2}-\d{2}/.test(val)) continue
        // 同じテキストの重複を排除
        if (seen.has(val)) continue
        seen.add(val)
        texts.push(val)
      }
    }
    return texts
  }

  updateAnswerColumnsInput() {
    if (!this.hasAnswerColumnsInputTarget) return
    const cols = this.detectedAnswerCols || []
    const data = cols.map(col => ({
      index: col.index,
      header: col.header,
      description: col.description || null
    }))
    this.answerColumnsInputTarget.value = JSON.stringify(data)
  }

  renderImportPreview() {
    if (!this.hasImportPreviewTarget) return

    const mappedFields = Object.entries(this.mappings).filter(([_, v]) => v !== "" && v !== undefined)
    const answerCols = this.detectedAnswerCols || []

    if (mappedFields.length === 0 && answerCols.length === 0) {
      this.importPreviewTarget.innerHTML = ""
      return
    }

    const sheet = this.sheet
    if (!sheet || !sheet["!ref"]) {
      this.importPreviewTarget.innerHTML = ""
      return
    }

    const range = XLSX.utils.decode_range(sheet["!ref"])
    const dataStartRowIdx = (parseInt(this.getDataStartRowValue()) || 5) - 1
    const labels = this.constructor.FIELD_LABELS

    // 個人情報フィールド（マッピング済みかつ PERSONAL_INFO_FIELDS に含まれるもの）
    const personalInfoFields = this.constructor.PERSONAL_INFO_FIELDS
    const personalFields = mappedFields.filter(([field]) => personalInfoFields.includes(field))
    const segmentEntries = this.segmentColumns ? Array.from(this.segmentColumns.entries()) : []
    const hasSegments = segmentEntries.length > 0

    // 全データ行を走査して取り込み対象の行だけ収集
    const validRows = []
    for (let r = dataStartRowIdx; r <= range.e.r; r++) {
      const hasPersonalInfo = personalFields.some(([_, colIndex]) => {
        const ref = XLSX.utils.encode_cell({ r, c: parseInt(colIndex) })
        const cell = sheet[ref]
        return cell && cell.v !== null && cell.v !== undefined && String(cell.v).trim() !== ""
      })
      if (!hasPersonalInfo && personalFields.length > 0) continue

      // 割り付け列が検出されている場合、いずれかの列に値がある行のみ
      if (hasSegments) {
        let hasAnySegment = false
        for (const [colIdx] of segmentEntries) {
          const sRef = XLSX.utils.encode_cell({ r, c: colIdx })
          const sCell = sheet[sRef]
          if (sCell && sCell.v != null && String(sCell.v).trim() !== "") {
            hasAnySegment = true
            break
          }
        }
        if (!hasAnySegment) continue
      }

      validRows.push(r)
    }

    const totalDataRows = validRows.length
    const previewRowData = validRows.slice(0, 30)

    let html = '<div class="rounded-xl bg-white shadow ring-1 ring-gray-900/5 overflow-hidden">'
    html += '<div class="px-6 pt-5 pb-3">'
    html += '<div class="flex items-center justify-between mb-2">'
    html += '<h2 class="text-base font-semibold text-gray-900">取り込みプレビュー</h2>'
    html += `<span class="text-sm text-gray-500">${totalDataRows}件が取り込み対象</span>`
    html += '</div>'
    html += '<p class="text-sm text-gray-500">実際に取り込まれるデータのプレビューです。</p>'
    html += '<p class="text-xs text-gray-500 mt-2">取り込み対象の最初の30行を表示しています。内容に問題がないか確認してください。</p>'
    if (!hasSegments && this.hasSegmentInfoTarget) {
      html += '<div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 mt-3 flex items-start gap-2">'
      html += '<svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
      html += '<p class="text-xs text-amber-700">6 行目に割り付け名のセルが見つかりませんでした。設定すると割り付けがある候補者のみが取り込まれます。</p>'
      html += '</div>'
    }
    html += '</div>'

    html += '<div class="overflow-auto max-h-[500px]">'
    html += '<table class="min-w-full text-xs border-collapse">'

    html += '<thead><tr class="bg-gray-50">'
    html += '<th class="px-3 py-2 text-left font-medium text-gray-500 border border-gray-200 sticky left-0 z-10 bg-gray-50">行</th>'
    for (const [field] of mappedFields) {
      html += `<th class="px-3 py-2 text-left font-medium text-gray-500 border border-gray-200 whitespace-nowrap">${this.escapeHtml(labels[field] || field)}</th>`
    }
    // 割り付け列ヘッダー
    const segmentColNames = segmentEntries.map(([colIdx, info]) => ({ index: colIdx, seq: info.seq, name: info.name }))
    for (const seg of segmentColNames) {
      html += `<th class="px-3 py-2 text-center font-medium text-indigo-700 border border-gray-200 bg-indigo-50 whitespace-nowrap" title="${this.escapeHtml(seg.name)}">`
      html += `割り付け${seg.seq}`
      html += '</th>'
    }
    for (const col of answerCols) {
      const label = col.description ? `${col.header} (${col.description})` : col.header
      html += `<th class="px-3 py-2 text-left font-medium text-amber-700 border border-gray-200 bg-amber-50 whitespace-nowrap" title="${this.escapeHtml(label)}">`
      html += `<div>${this.escapeHtml(col.header)}</div>`
      if (col.description) {
        html += `<div class="font-normal text-[10px] text-amber-500 truncate max-w-[120px]">${this.escapeHtml(col.description)}</div>`
      }
      html += '</th>'
    }
    html += '</tr></thead><tbody>'

    for (const r of previewRowData) {
      const displayRow = r + 1
      html += '<tr>'
      html += `<td class="px-3 py-2 text-gray-400 font-mono border border-gray-200 bg-gray-50 sticky left-0 z-[5]">${displayRow}</td>`

      for (const [field, colIndex] of mappedFields) {
        const ref = XLSX.utils.encode_cell({ r, c: parseInt(colIndex) })
        const cell = sheet[ref]
        const raw = cell ? this.formatValue(cell) : ""
        const value = raw.replace(/^'+/, "")
        const isEmpty = value.trim() === ""

        html += `<td class="px-3 py-2 border border-gray-200 whitespace-nowrap ${isEmpty ? "text-gray-300" : "text-gray-900"}">`
        html += isEmpty ? "-" : this.escapeHtml(value)
        html += '</td>'
      }

      // 割り付け列データ
      for (const seg of segmentColNames) {
        const ref = XLSX.utils.encode_cell({ r, c: seg.index })
        const cell = sheet[ref]
        const raw = cell ? this.formatValue(cell) : ""
        const value = raw.replace(/^'+/, "")
        const isEmpty = value.trim() === ""

        html += `<td class="px-3 py-2 border border-gray-200 bg-indigo-50/50 whitespace-nowrap font-semibold ${isEmpty ? "text-gray-300" : "text-indigo-700"}">`
        html += isEmpty ? "-" : this.escapeHtml(value)
        html += '</td>'
      }

      for (const col of answerCols) {
        const ref = XLSX.utils.encode_cell({ r, c: col.index })
        const cell = sheet[ref]
        const raw = cell ? this.formatValue(cell) : ""
        const value = raw.replace(/^'+/, "")
        const isEmpty = value.trim() === ""

        html += `<td class="px-3 py-2 border border-gray-200 bg-amber-50/50 whitespace-nowrap ${isEmpty ? "text-gray-300" : "text-amber-900"}">`
        html += isEmpty ? "-" : this.escapeHtml(value)
        html += '</td>'
      }
      html += '</tr>'
    }

    html += '</tbody></table></div>'

    html += '</div>'
    this.importPreviewTarget.innerHTML = html
  }
}
